import React, { useState, useEffect } from 'react';
import {
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  User,
  Mail,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  AlertCircle,
  MessageSquare
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { adminBusinessClaimsApi } from '../../services/adminApi';

const AdminBusinessClaimsPage = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const data = await adminBusinessClaimsApi.getAll({
        search,
        status: statusFilter,
        page,
        limit: 20
      });
      setClaims(data.claims || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch claims:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, [search, statusFilter, page]);

  const handleApprove = async (claimId) => {
    if (!confirm('Bu talebi onaylamak istediğinizden emin misiniz? Kullanıcı işletme sahibi olarak atanacak.')) {
      return;
    }
    
    setActionLoading(claimId);
    try {
      await adminBusinessClaimsApi.approve(claimId);
      alert('Talep onaylandı! Kullanıcı işletme sahibi olarak atandı.');
      fetchClaims();
    } catch (error) {
      console.error('Failed to approve claim:', error);
      alert('Onaylama başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selectedClaim) return;
    
    setActionLoading(selectedClaim.id);
    try {
      await adminBusinessClaimsApi.reject(selectedClaim.id, rejectNote);
      alert('Talep reddedildi.');
      setShowRejectModal(false);
      setSelectedClaim(null);
      setRejectNote('');
      fetchClaims();
    } catch (error) {
      console.error('Failed to reject claim:', error);
      alert('Reddetme başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (claim) => {
    setSelectedClaim(claim);
    setRejectNote('');
    setShowRejectModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
            <Clock className="w-3 h-3" /> Bekliyor
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" /> Onaylandı
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">
            <XCircle className="w-3 h-3" /> Reddedildi
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">İşletme Talepleri</h1>
          <p className="text-slate-400 text-sm mt-1">Kullanıcıların işletme sahipliği taleplerini yönetin</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-3 py-1.5 bg-slate-700 rounded-lg text-slate-300">
            Toplam: {total}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İşletme veya kullanıcı ara..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="pending">Bekleyenler</option>
          <option value="approved">Onaylananlar</option>
          <option value="rejected">Reddedilenler</option>
          <option value="all">Tümü</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Bekleyen</p>
              <p className="text-xl font-bold text-white">
                {claims.filter(c => c.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Onaylanan</p>
              <p className="text-xl font-bold text-white">
                {claims.filter(c => c.status === 'approved').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Reddedilen</p>
              <p className="text-xl font-bold text-white">
                {claims.filter(c => c.status === 'rejected').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Claims List */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : claims.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <FileText className="w-12 h-12 mb-4" />
            <p>Talep bulunamadı</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {claims.map((claim) => (
              <div key={claim.id} className="p-4 hover:bg-slate-700/50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Claim Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{claim.business_name || 'İşletme'}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusBadge(claim.status)}
                        </div>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{claim.user_name || 'Kullanıcı'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <span>{claim.user_email || '-'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(claim.created_at)}</span>
                      </div>
                    </div>

                    {/* Message */}
                    {claim.message && (
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <p className="text-slate-300 text-sm">{claim.message}</p>
                        </div>
                      </div>
                    )}

                    {/* Admin Note (if rejected) */}
                    {claim.status === 'rejected' && claim.admin_note && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <p className="text-red-300 text-sm">Red nedeni: {claim.admin_note}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {claim.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleApprove(claim.id)}
                        disabled={actionLoading === claim.id}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        {actionLoading === claim.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Onayla
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => openRejectModal(claim)}
                        disabled={actionLoading === claim.id}
                        variant="destructive"
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reddet
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-slate-400 px-4">
            Sayfa {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedClaim && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">Talebi Reddet</h3>
              <p className="text-slate-400 text-sm mt-1">
                {selectedClaim.business_name} - {selectedClaim.user_name}
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-2 block">
                  Red nedeni (opsiyonel)
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="Kullanıcıya gönderilecek açıklama..."
                  rows={3}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedClaim(null);
                    setRejectNote('');
                  }}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  İptal
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={actionLoading === selectedClaim.id}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                >
                  {actionLoading === selectedClaim.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Reddet'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessClaimsPage;
